import type {
	GamingConsoleSettings,
	PCSettings,
	SellerOutput,
} from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import AddPaymentDialog from "@/pages/payment/shared/AddPaymentDialog";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { useGetAggregatedSellers } from "@/services";
import { addOrderDialogAtom } from "@/store/order";
import { formatNumber } from "@/utils/tools";
import {
	Button,
	ErrorBoundary,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@st-marketplace/ui";
import { useSetAtom } from "jotai";
import type { FC } from "react";
import {
	DashboardTable,
	EmptyRow,
	LoadingRows,
	SectionCard,
} from "./DashboardPrimitives";

const getPsSetting = (platform: Array<GamingConsoleSettings | PCSettings>) =>
	platform.find(
		(p): p is GamingConsoleSettings & { type_: "ps" } => p.type_ === "ps",
	);

/** What your sellers charge and what you owe them. */
export const DashboardSellerPrices: FC = () => {
	const { t } = useTranslation();
	const { getErrorInterface } = useApiError();
	const openAddOrder = useSetAtom(addOrderDialogAtom);

	const { data, isLoading, error, refetch } = useGetAggregatedSellers({
		query: { page: 1, page_size: 5, statuses: ["ACCEPTED"] },
	});

	const sellers: SellerOutput[] = data?.results ?? [];
	const tableError = error ? getErrorInterface(error) : undefined;
	if (tableError) {
		return (
			<SectionCard
				title={t("home.sellersTitle")}
				viewAllTo="/relations/seller"
				action={<AddPaymentDialog direction="send" size="sm" />}
			>
				<ErrorBoundary isLoading={false} error={tableError} onReset={refetch}>
					<div />
				</ErrorBoundary>
			</SectionCard>
		);
	}

	return (
		<SectionCard
			title={t("home.sellersTitle")}
			viewAllTo="/relations/seller"
			action={<AddPaymentDialog direction="send" size="sm" />}
		>
			<DashboardTable>
				<TableHeader>
					<TableRow>
						<TableHead>{t("relation.tableSellerName")}</TableHead>
						<TableHead>{t("relation.tableUnitPrice")}</TableHead>
						<TableHead>{t("relation.tableAvailableStock")}</TableHead>
						<TableHead>{t("relation.tableMinimumToOrderPs")}</TableHead>
						<TableHead>{t("relation.tableMaximumToOrderPs")}</TableHead>
						<TableHead>{t("relation.tableDebt")}</TableHead>
						<TableHead>{t("relation.tableMaximumDebt")}</TableHead>
						<TableHead />
					</TableRow>
				</TableHeader>
				<TableBody>
					{isLoading ? (
						<LoadingRows columns={8} />
					) : sellers.length === 0 ? (
						<EmptyRow colSpan={8}>{t("home.noSellers")}</EmptyRow>
					) : (
						sellers.map((seller) => {
							const ps = getPsSetting(seller.platform);
							return (
								<TableRow key={seller.seller_id}>
									<TableCell data-label={t("relation.tableSellerName")}>
										<RelationNameLink
											name={seller.seller_display_name}
											relationId={seller.relation_id}
											party="seller"
										/>
									</TableCell>
									<TableCell data-label={t("relation.tableUnitPrice")}>
										{formatNumber(ps?.unit_price, "price")}
									</TableCell>
									<TableCell data-label={t("relation.tableAvailableStock")}>
										{formatNumber(ps?.available_coins?.value ?? 0, "count")}
									</TableCell>
									<TableCell data-label={t("relation.tableMinimumToOrderPs")}>
										{formatNumber(ps?.minimum_to_order, "count")}
									</TableCell>
									<TableCell data-label={t("relation.tableMaximumToOrderPs")}>
										{formatNumber(ps?.maximum_to_order, "count")}
									</TableCell>
									<TableCell data-label={t("relation.tableDebt")}>
										{formatNumber(seller.current_debt, "price")}
									</TableCell>
									<TableCell data-label={t("relation.tableMaximumDebt")}>
										{formatNumber(seller.max_debt, "price")}
									</TableCell>
									<TableCell className="text-end">
										<Button
											type="button"
											size="sm"
											color="secondary"
											onClick={() => openAddOrder(true)}
										>
											{t("home.orderButton")}
										</Button>
									</TableCell>
								</TableRow>
							);
						})
					)}
				</TableBody>
			</DashboardTable>
		</SectionCard>
	);
};
